import React from "react";
import { NavLink } from "react-router-dom";
import { useI18n } from "@/i18n";

export default function Footer() {
  const { lang, t } = useI18n();
  const base = `/${lang}`;

  const navLinks = [
    { key: "home", to: base },
    { key: "how", to: `${base}#how` },
    { key: "projects", to: `${base}#projects` },
    { key: "about", to: `${base}#about` },
  ];

  const legalLinks = [
    { key: "privacy", to: `${base}/privacy` },
    { key: "cookies", to: `${base}/cookies` },
    { key: "terms", to: `${base}/terms` },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="bg-slate-900 text-slate-300"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-white text-slate-900 font-serif text-lg"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                H
              </span>
              <span
                className="text-xl text-white"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("footer.brand")}
              </span>
            </div>
            <p className="text-sm text-slate-400 max-w-md leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="text-xs text-slate-400 space-y-1 leading-relaxed">
              <div data-testid="footer-legal-company">
                {t("footer.legalDetails.companyName")}
              </div>
              <div data-testid="footer-legal-registration">
                {t("footer.legalDetails.registration")}
              </div>
              <div data-testid="footer-legal-tax">
                {t("footer.legalDetails.taxId")}
              </div>
              <div data-testid="footer-legal-license">
                {t("footer.legalDetails.license")}
              </div>
              <div data-testid="footer-legal-address">
                {t("footer.legalDetails.address")}
              </div>
              <div data-testid="footer-legal-jurisdiction">
                {t("footer.legalDetails.jurisdiction")}
              </div>
              <div data-testid="footer-legal-year">
                {t("footer.legalDetails.incorporated")}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xs tracking-[0.2em] uppercase text-slate-500 mb-5">
              {t("footer.navTitle")}
            </h3>
            <ul className="space-y-3 text-sm">
              {navLinks.map((l) => (
                <li key={l.key}>
                  <a
                    href={l.to}
                    data-testid={`footer-nav-${l.key}`}
                    className="text-slate-300 hover:text-white"
                  >
                    {t(`footer.links.${l.key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs tracking-[0.2em] uppercase text-slate-500 mb-5">
              {t("footer.legalTitle")}
            </h3>
            <ul className="space-y-3 text-sm">
              {legalLinks.map((l) => (
                <li key={l.key}>
                  <NavLink
                    to={l.to}
                    data-testid={`footer-legal-${l.key}`}
                    className="text-slate-300 hover:text-white"
                  >
                    {t(`footer.links.${l.key}`)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-800 space-y-4">
          <p
            data-testid="footer-disclaimer"
            className="text-xs text-slate-500 leading-relaxed max-w-4xl"
          >
            {t("footer.disclaimer")}
          </p>
          <p
            data-testid="footer-copyright"
            className="text-xs text-slate-500"
          >
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
}
